import ActiveItemDetails from 'components/active-item/ActiveItemDetails';
import ActiveItemProperties from 'components/active-item/ActiveItemProperties';
import useActiveItem from 'hooks/useActiveItem';
import useProfile from 'hooks/useProfile';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const MakeOfferCreate = ({ nextStep, closeWindow, offerData, setOfferData }) => {
  const { pathname } = useLocation();
  const { amount, days, hours } = offerData;
  const { balance } = useProfile();
  const { activeItem } = useActiveItem();

  // ВАЖНО: amount теперь ожидается как СТРОКА (например, "123.00")
  // Если изначально amount — число, приведём его к строке один раз
  useEffect(() => {
    if (typeof offerData.amount === 'number') {
      setOfferData(prev => ({
        ...prev,
        amount: offerData.amount === 0 ? "0.00" : offerData.amount.toFixed(2)
      }));
    }
  }, []); // запускаем один раз

  const isDisabledSubmitCheck = () => {
    const numAmount = parseFloat(amount?.replace(',', '.') || '');
    const isAmountValid = !isNaN(numAmount) && numAmount > 0;
    const areTimeValid =
      (days !== null || hours !== null) &&
      !(days === 0 && hours === 0) &&
      !((days === null || days === '') && (hours !== null && hours !== '')) &&
      !((hours === null || hours === '') && (days !== null && days !== ''));

    setIsSubmitDisabled(!isAmountValid || !areTimeValid);
  };

  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

  useEffect(() => {
    isDisabledSubmitCheck();
  }, [offerData]);

  // Безопасное получение числа
  const getNumericValue = (str) => {
    if (!str || typeof str !== 'string') return NaN;
    const normalized = str.trim().replace(/,/g, '.');
    return parseFloat(normalized);
  };

  const onInputHandler = (e, max) => {
    const { name, value } = e.target;

    if (name === 'amount') {
      // Очищаем от всего, кроме цифр, точки и запятой
      let cleanedValue = value.replace(/[^0-9.,]/g, '');
      const parts = cleanedValue.split(/[,\.]/);

      // Защита: не более одного разделителя, не более 2 знаков после
      if (parts.length > 2 || (parts.length === 2 && parts[1].length > 2)) {
        return;
      }

      // Если пусто — сохраняем как пустую строку (или null, но лучше "")
      if (cleanedValue === '') {
        setOfferData(prev => ({ ...prev, amount: "" }));
        return;
      }

      // Не преобразуем в число! Храним как строку
      setOfferData(prev => ({ ...prev, amount: cleanedValue }));
      return;
    }

    // Для days/hours — как раньше (числа)
    let cleanedValue = value.replace(/[^0-9]/g, '');
    if (cleanedValue === '') {
      setOfferData(prev => ({ ...prev, [name]: null }));
      return;
    }

    const num = Number(cleanedValue);
    if (num > max) {
      setOfferData(prev => ({ ...prev, [name]: max }));
    } else {
      setOfferData(prev => ({ ...prev, [name]: num }));
    }
  };

  const handleAmountBlur = () => {
    if (!amount || amount.trim() === '') {
      setOfferData(prev => ({ ...prev, amount: "" }));
      return;
    }

    const num = getNumericValue(amount);
    if (isNaN(num)) {
      setOfferData(prev => ({ ...prev, amount: "" }));
      return;
    }

    // Форматируем до 2 знаков — возвращаем строку!
    setOfferData(prev => ({ ...prev, amount: num.toFixed(2) }));
  };

  return (
    <section className="make-offer" onClick={(e) => e.stopPropagation()}>
      <section className="make-offer__details">
        <ActiveItemDetails
          pathname={pathname}
          name={activeItem.name}
          img={activeItem.image}
          packType={activeItem.pack_type}
          tag={activeItem.tag}
          type={activeItem.type}
          properties={activeItem.properties}
        />
        <ActiveItemProperties properties={activeItem.properties} />
      </section>
      <section className="make-offer__info">
        <header className="make-offer__header">
          <h2 className="make-offer__title">Make an Offer</h2>
          <button className="make-offer__close" onClick={closeWindow}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"></path>
            </svg>
          </button>
        </header>
        <p className="make-offer__balance">
          <span>Your account balance is:</span>
          <b>{`$${balance} USD`}</b>
        </p>
        <form className="make-offer__form">
          <div className="make-offer__form-row">
            <label>Your offer</label>
            <p className="make-offer__input-wrapper">
              {/* ⚠️ type="text", не "number" */}
              <input
                type="text"
                name="amount"
                onInput={(e) => onInputHandler(e, Infinity)}
                onBlur={handleAmountBlur}
                placeholder="Offer value"
                value={amount || ""}
              />
              <span>USD</span>
            </p>
          </div>
          <div className="make-offer__form-row make-offer__form-row--double">
            <label>Offer valid for</label>
            <p className="make-offer__input-wrapper">
              <input
                type="text"
                name="days"
                onInput={(e) => onInputHandler(e, 30)}
                placeholder="0-30"
                value={days ?? ''}
              />
              <span>DAYS</span>
              <i>Max 30 days</i>
            </p>
            <p className="make-offer__input-wrapper">
              <input
                type="text"
                name="hours"
                onInput={(e) => onInputHandler(e, 23)}
                placeholder="0-23"
                value={hours ?? ''}
              />
              <span>HOURS</span>
              <i>Max 23 hours</i>
            </p>
          </div>

          <p className='make-offer__form-hint'>
            <span>Seller may receive multiple offers and may accept any offer submitted, regardless of the order in which the offer was received</span>
          </p>

          <button
            type="button"
            className="make-offer__form-submit"
            disabled={isSubmitDisabled}
            onClick={nextStep}
          >
            Continue
          </button>
        </form>
      </section>
    </section>
  );
};

export default MakeOfferCreate;

// import ActiveItemDetails from 'components/active-item/ActiveItemDetails';
// import ActiveItemProperties from 'components/active-item/ActiveItemProperties';
// import useActiveItem from 'hooks/useActiveItem';
// import useProfile from 'hooks/useProfile';
// import React, { useEffect, useState } from 'react'
// import { useLocation } from 'react-router-dom';

// const MakeOfferCreate = ({ nextStep, closeWindow, offerData, setOfferData }) => {
//   const { pathname } = useLocation();
//   const {amount, days, hours} = offerData;
//   const {balance} = useProfile();
//   const {activeItem} = useActiveItem();
//   const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

//   const isDisabledSubmitCheck = () => {
//     setIsSubmitDisabled(
//       (amount === null || amount === 0)
//       ||
//       (
//         (days !== null || days !== 0) && hours === null
//       )
//       ||
//       (
//         (hours !== null || hours !== 0) && days === null
//       )
//       ||
//       (days === 0 && hours === 0)
//     );
// };

//   const onInputHandler = (e, max) => {
//     const { name, value } = e.target;

//     let cleanedValue = value.replace(/[^0-9.,]/g, '');

//     if (name === 'amount') {
//       const decimalParts = cleanedValue.split(/[,\.]/);
//       if (decimalParts.length > 2 || (decimalParts.length === 2 && decimalParts[1].length > 2)) {
//         return;
//       }
//     }


//     if (cleanedValue === '') {
//       setOfferData((prevData) => ({
//         ...prevData,
//         [name]: null
//       }));
//       return;
//     }


//     const numericValue = Number(cleanedValue.replace(',', '.'));


//     if (numericValue > max) {
//       setOfferData((prevData) => ({
//         ...prevData,
//         [name]: max
//       }));
//       return;
//     }


//     setOfferData((prevData) => ({
//       ...prevData,
//       [name]: numericValue
//     }));
//   };

//   useEffect(() => {
//     isDisabledSubmitCheck();
//   }, [offerData]);

//   return (
//     <section className="make-offer" onClick={(e) => e.stopPropagation()}>
//       <section className="make-offer__details">
//         <ActiveItemDetails
//           pathname={pathname}
//           name={activeItem.name}
//           img={activeItem.image}
//           packType={activeItem.pack_type}
//           tag={activeItem.tag}
//           type={activeItem.type}
//           properties={activeItem.properties}
//         />
//         <ActiveItemProperties properties={activeItem.properties} />
//       </section>
//       <section className="make-offer__info">
//         <header className="make-offer__header">
//           <h2 className="make-offer__title">Make an Offer</h2>
//           <button className="make-offer__close" onClick={closeWindow}>
//             <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
//               <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"></path>
//             </svg>
//           </button>
//         </header>
//         <p className="make-offer__balance">
//           <span>Your account balance is:</span>
//           <b>{`$${balance} USD`}</b>
//         </p>
//         <form className="make-offer__form">
//           <div className="make-offer__form-row">
//             <label>Your offer</label>
//             <p className="make-offer__input-wrapper">
//               <input type="number" name='amount' onInput={(e) => onInputHandler(e, Infinity)} placeholder="Offer value" value={amount} />
//               <span>USD</span>
//             </p>
//           </div>
//           <div className="make-offer__form-row make-offer__form-row--double">
//             <label>Offer valid for</label>
//             <p className="make-offer__input-wrapper">
//               <input type="text" name="days" onInput={(e) => onInputHandler(e, 30)} placeholder="0-30" value={days} />
//               <span>DAYS</span>
//               <i>Max 30 days</i>
//             </p>
//             <p className="make-offer__input-wrapper">
//               <input type="text" name="hours" onInput={(e) => onInputHandler(e, 23)} placeholder="0-23" value={hours} />
//               <span>HOURS</span>
//               <i>Max 23 hours</i>
//             </p>
//           </div>

//           <p className='make-offer__form-hint'>
//             <span>Seller may receive multiple offers and may accept any offer submitted, regardless of the order in which the offer was received</span>
//           </p>

//           <button
//             type="button"
//             className="make-offer__form-submit"
//             disabled={isSubmitDisabled}
//             onClick={nextStep}
//           >
//             Continue
//           </button>
//         </form>
//       </section>
//     </section>
//   )
// }

// export default MakeOfferCreate;
